import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./buscadorFiltro.css";
import Input from "../../Elements/Input/Input";
import Fuse from "fuse.js";

const DEFAULT_KEYS = [
  { name: "nombre", weight: 0.35 },
  { name: "apellidoPaterno", weight: 0.2 },
  { name: "apellidoMaterno", weight: 0.1 },
  { name: "puesto", weight: 0.2 },
  { name: "area", weight: 0.1 },
  { name: "numeroEmpleado", weight: 0.05 },
];

export default function BuscadorYFiltro({
  search,
  setSearch,
  filterActivo,
  setFilterActivo,
  data = [],
  onResults,
  keys = DEFAULT_KEYS,
  maxSuggestions = 5,
  minChars = 2,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const stripAccents = useCallback((str = "") => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }, []);

  const tokenRegex = useMemo(() => /"([^"]+)"|(\S+)/g, []);

  const parseTokens = useCallback(
    (q) => {
      const tokens = [];
      if (!q) return tokens;

      let match;
      tokenRegex.lastIndex = 0;

      while ((match = tokenRegex.exec(q))) {
        const raw = match[1] || match[2];
        const [k, ...rest] = raw.split(":");

        if (rest.length) {
          tokens.push({
            type: "kv",
            key: k.toLowerCase(),
            value: stripAccents(rest.join(":")),
          });
        } else {
          tokens.push({
            type: "word",
            value: stripAccents(raw),
          });
        }
      }

      return tokens;
    },
    [stripAccents, tokenRegex]
  );

  const stableKeys = useMemo(() => keys || DEFAULT_KEYS, [keys]);

  const fuseKeys = useMemo(() => {
    return stableKeys.map((k) =>
      typeof k === "string" ? { name: k, weight: 1 } : k
    );
  }, [stableKeys]);

  const fuse = useMemo(() => {
    if (!data?.length) return null;

    return new Fuse(data, {
      includeScore: true,
      threshold: 0.35,
      ignoreLocation: true,
      keys: fuseKeys,
      getFn: (obj, path) => stripAccents(String(obj?.[path] ?? "")),
    });
  }, [data, fuseKeys, stripAccents]);

  const filterDeterministic = useCallback(
    (q, base = data) => {
      if (!base?.length) return [];

      const tokens = parseTokens(q);
      if (!tokens.length) return base;

      const fields = fuseKeys.map((k) => k.name);

      return base.filter((row) => {
        const bag = fields
          .map((f) => stripAccents(String(row?.[f] ?? "")))
          .join(" ");

        return tokens.every((t) => {
          if (t.type === "word") return bag.includes(t.value);

          if (t.type === "kv") {
            const v = stripAccents(String(row?.[t.key] ?? ""));
            return v.includes(t.value);
          }

          return true;
        });
      });
    },
    [data, fuseKeys, parseTokens, stripAccents]
  );

  const isFieldLikeQuery = useCallback(
    (q) => {
      const tokens = parseTokens(q);
      if (!tokens.length) return false;
      if (tokens.some((t) => t.type === "kv")) return true;

      if (tokens.length === 1 && data?.length) {
        const word = tokens[0].value;

        for (const row of data) {
          const puesto = stripAccents(String(row?.puesto ?? ""));
          const area = stripAccents(String(row?.area ?? ""));

          if (puesto.includes(word) || area.includes(word)) {
            return true;
          }
        }
      }

      return false;
    },
    [data, parseTokens, stripAccents]
  );

  const buildSuggestions = useCallback(
    (q) => {
      if (!fuse || !q || stripAccents(q).length < minChars) return [];

      const nameKeys = [
        "nombre",
        "apellidoPaterno",
        "apellidoMaterno",
        "puesto",
        "area",
      ];

      const nameFuse = new Fuse(data, {
        includeScore: true,
        threshold: 0.4,
        ignoreLocation: true,
        keys: nameKeys,
        getFn: (obj, path) => stripAccents(String(obj?.[path] ?? "")),
      });

      const tokens = parseTokens(q).filter((t) => t.type === "word");
      const namePart = tokens.length ? tokens[0].value : stripAccents(q);

      const primary = nameFuse.search(namePart).slice(0, maxSuggestions);

      return primary.map((r) => {
        const nom = r.item?.nombre ?? "";
        const ap1 = r.item?.apellidoPaterno ?? "";
        const ap2 = r.item?.apellidoMaterno ?? "";
        const puesto = r.item?.puesto ?? "";
        const area = r.item?.area ?? "";
        const num = r.item?.numeroEmpleado ?? "";

        return {
          label: `${nom} ${ap1} ${ap2}${puesto ? ` (${puesto}${area ? " · " + area : ""})` : ""}${num ? ` · #${num}` : ""}`.trim(),
          value: `${nom} ${ap1} ${ap2} ${puesto}`.trim(),
        };
      });
    },
    [data, fuse, maxSuggestions, minChars, parseTokens, stripAccents]
  );

  useEffect(() => {
    if (!data?.length) return;

    const deterministic = filterDeterministic(search, data);

    if (typeof onResults === "function") {
      const filtered = deterministic.filter((row) => {
        if (filterActivo === "all") return true;
        if (filterActivo === "true") return !!row.activo;
        if (filterActivo === "false") return !row.activo;
        return true;
      });

      onResults(filtered);
    }

    const shouldSuggest =
      stripAccents(search).length >= minChars &&
      deterministic.length === 0 &&
      !isFieldLikeQuery(search);

    if (shouldSuggest) {
      const newSuggestions = buildSuggestions(search);

      setSuggestions((prev) => {
        const prevSerialized = JSON.stringify(prev);
        const nextSerialized = JSON.stringify(newSuggestions);
        return prevSerialized === nextSerialized ? prev : newSuggestions;
      });

      setShowSuggestions((prev) => (prev ? prev : true));
    } else {
      setSuggestions((prev) => (prev.length ? [] : prev));
      setShowSuggestions((prev) => (prev ? false : prev));
      setActiveIndex((prev) => (prev !== -1 ? -1 : prev));
    }
  }, [
    data,
    filterActivo,
    search,
    minChars,
    onResults,
    filterDeterministic,
    isFieldLikeQuery,
    stripAccents,
    buildSuggestions,
  ]);

  useEffect(() => {
    const handler = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      setSearch(suggestion.value);
      setShowSuggestions(false);
      setActiveIndex(-1);
      inputRef.current?.focus();
    },
    [setSearch]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!showSuggestions || !suggestions.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIndex >= 0) {
          handleSelectSuggestion(suggestions[activeIndex]);
        } else {
          setShowSuggestions(false);
        }
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [showSuggestions, suggestions, activeIndex, handleSelectSuggestion]
  );

  const clearSearch = useCallback(() => {
    setSearch("");
    setActiveIndex(-1);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [setSearch]);

  return (
    <div className="buscador-filtros" ref={wrapperRef}>
      <div className="buscador-input" style={{ position: "relative" }}>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => {
            if (suggestions.length) setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Busca por nombre, apellidos, área, puesto o # de empleado"
          classNames="buscador-input-box"
        />

        {search?.length > 0 && (
          <button
            type="button"
            aria-label="Limpiar búsqueda"
            className="btn-clear"
            onClick={clearSearch}
            style={{
              position: "absolute",
              right: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              fontSize: 16,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div
            className="sugerencias-dropdown"
            role="listbox"
            style={{
              position: "absolute",
              zIndex: 10,
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              marginTop: 6,
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "8px 12px", fontSize: 12, color: "#6B7280" }}>
              Quizá quisiste escribir...
            </div>

            {suggestions.slice(0, maxSuggestions).map((s, i) => (
              <button
                key={`${s.value}-${i}`}
                role="option"
                aria-selected={activeIndex === i}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(-1)}
                onClick={() => handleSelectSuggestion(s)}
                className={`sugerencia-item ${activeIndex === i ? "is-active" : ""}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: 0,
                  background: activeIndex === i ? "#F3F4F6" : "white",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <select
        className="select-filtro"
        value={filterActivo}
        onChange={(e) => setFilterActivo(e.target.value)}
      >
        <option value="all">Todos</option>
        <option value="true">Solo activos</option>
        <option value="false">Solo inactivos</option>
      </select>
    </div>
  );
}